import React from 'react';
import Header from '../components/Header';
import Footer from '../components/Footer';
import useSeo from '../hooks/useSeo';
import {
  Shield, FileText, Users, Lock, CreditCard, Bot, Database,
  MessageSquare, Cookie, Scale, AlertTriangle, Mail, Globe, Bell, RefreshCw, Briefcase
} from 'lucide-react';

const Section = ({ icon: Icon, number, title, children }) => (
  <div className="mb-10" data-testid={`terms-section-${number}`}>
    <div className="flex items-center gap-3 mb-4">
      <div className="w-9 h-9 bg-green-100 rounded-lg flex items-center justify-center flex-shrink-0">
        <Icon className="w-[18px] h-[18px] text-green-700" />
      </div>
      <h2 className="text-lg sm:text-xl font-bold text-gray-900">{number}. {title}</h2>
    </div>
    <div className="pl-12 text-gray-700 leading-relaxed space-y-3 text-sm sm:text-base">{children}</div>
  </div>
);

const TermsAndConditions = () => {
  useSeo({
    title: 'Terms & Conditions — KnowMySchemes',
    description: 'KnowMySchemes Terms & Conditions and Privacy Policy. Active version: v6 - April 2026. Read about service offerings, refund policy, AI features, and user rights.',
    canonical: 'https://knowmyschemes.com/terms',
  });
  return (
    <div className="min-h-screen bg-gray-50">
      <Header />
      <div className="max-w-3xl mx-auto px-4 py-12 sm:py-16">
        <div className="text-center mb-10">
          <div className="inline-flex items-center justify-center w-14 h-14 bg-green-100 rounded-full mb-4">
            <FileText className="w-7 h-7 text-green-700" />
          </div>
          <h1 className="text-3xl sm:text-4xl font-bold text-gray-900 mb-2" data-testid="terms-title">Terms & Conditions and Privacy Policy</h1>
          <p className="text-gray-500 text-sm">Last Updated: April 2026 | Effective Date: 17 April 2026</p>
        </div>

        <div className="bg-white rounded-xl border border-gray-200 p-6 sm:p-8" data-testid="terms-content">
          {/* Disclaimer */}
          <div className="bg-red-50 border border-red-200 rounded-lg p-4 mb-10">
            <div className="flex items-start gap-3">
              <AlertTriangle className="w-5 h-5 text-red-600 mt-0.5 flex-shrink-0" />
              <div>
                <p className="font-bold text-red-800 mb-1">IMPORTANT DISCLAIMER</p>
                <p className="text-red-700 text-sm leading-relaxed">
                  KnowMySchemes is a PRIVATE service provider. We are NOT a government entity, government portal, or affiliated with any government department. We do NOT guarantee approval of any scheme or application. Approval is solely at the discretion of the concerned government authority.
                </p>
              </div>
            </div>
          </div>

          <Section icon={FileText} number={1} title="Introduction & Acceptance">
            <p>
              KMS (KnowMySchemes) is a digital platform that provides information, assistance, and support services to help citizens understand eligibility, prepare applications, upload documents, and access public and welfare-related government schemes.
            </p>
            <p>
              By accessing or using the KMS website or mobile application, you agree to be bound by these Terms, Conditions, and Privacy Policy. If you do not agree, you must discontinue use of the platform immediately.
            </p>
          </Section>

          <Section icon={Users} number={2} title="Eligibility to Use">
            <ul className="list-disc pl-5 space-y-1.5">
              <li>Users must be at least 18 years of age or have valid legal guardian consent.</li>
              <li>All information provided during registration must be accurate, complete, and up to date.</li>
              <li>Providing false, misleading, or incomplete information may result in suspension or termination of the account without notice.</li>
              <li>Users are responsible for maintaining the confidentiality of their login credentials and all activities conducted through their account.</li>
            </ul>
          </Section>

          <Section icon={Globe} number={3} title="Services Offered">
            <p>KMS offers the following services for facilitation purposes only:</p>
            <ul className="list-disc pl-5 space-y-1.5 mt-2">
              <li>Eligibility checking for government schemes</li>
              <li>Application assistance and form filling</li>
              <li>Document upload and verification support</li>
              <li>Information delivery and status tracking</li>
              <li>Notifications and alerts for relevant schemes</li>
              <li>Customer support and guided assistance</li>
              <li>AI-powered impact analysis (KMS Smart) — personalised policy impact reports based on user profile</li>
              <li>Income intelligence recommendations based on user occupation and location</li>
              <li>Government job listings aggregated from official government recruitment portals</li>
            </ul>
            <p className="mt-3 font-medium text-gray-800">
              KMS charges fees solely for the above assistance services. KMS does NOT sell government benefits, influence government decisions, or assure approvals. Payment for services does not guarantee acceptance or receipt of any government benefit.
            </p>
          </Section>

          <Section icon={Briefcase} number={4} title="Government Jobs Listing">
            <p>
              KnowMySchemes provides a Government Jobs listing section for informational purposes only. This feature aggregates publicly available job notifications from official government recruitment websites to help citizens stay informed about employment opportunities across India.
            </p>

            <p className="font-semibold text-gray-800 mt-4">4.1 Nature of Government Jobs Listings</p>
            <ul className="list-disc pl-5 space-y-1.5 mt-1">
              <li>All government job notifications listed on KnowMySchemes are sourced from publicly available official government recruitment websites including but not limited to UPSC, SSC, Railway Recruitment Boards, APPSC, TSPSC, KPSC, IBPS, and other central and state government portals.</li>
              <li>KnowMySchemes is NOT a government recruitment portal and is NOT affiliated with any government recruitment body or authority.</li>
              <li>Job listings are aggregated and displayed for informational convenience only.</li>
              <li>Users must verify all job details on the respective official government website before applying.</li>
              <li>All job application links redirect users to official government websites. KnowMySchemes does not process, collect, or handle any job applications or application fees.</li>
              <li>KnowMySchemes displays job notifications from central government bodies, state governments of Andhra Pradesh, Telangana, Karnataka, and other states across India.</li>
            </ul>

            <p className="font-semibold text-gray-800 mt-4">4.2 Accuracy and Disclaimer</p>
            <ul className="list-disc pl-5 space-y-1.5 mt-1">
              <li>While KnowMySchemes makes every effort to display accurate and up-to-date job notifications, we do not guarantee the completeness, accuracy, or timeliness of the information displayed.</li>
              <li>Job notifications may be updated, modified, or cancelled by the respective government authority at any time without prior notice to KnowMySchemes.</li>
              <li>KnowMySchemes shall NOT be liable for any loss, missed opportunity, or damage arising from reliance on job listings displayed on the platform.</li>
              <li>Always verify details on the official government recruitment website before the application deadline.</li>
              <li>KnowMySchemes does not charge any fee for viewing government job listings. Any entity claiming to offer government job assistance on behalf of KnowMySchemes for a fee is fraudulent.</li>
              <li>Job listing data is refreshed periodically. KnowMySchemes does not guarantee real-time accuracy of vacancy counts, deadlines, or eligibility criteria.</li>
            </ul>

            <p className="font-semibold text-gray-800 mt-4">4.3 Data Sources</p>
            <ul className="list-disc pl-5 space-y-1.5 mt-1">
              <li>Government job data is sourced from publicly available official government recruitment portals including UPSC (upsc.gov.in), SSC (ssc.gov.in), Railway Recruitment Boards (indianrailways.gov.in), APPSC (psc.ap.gov.in), TSPSC (tspsc.gov.in), KPSC (kpsc.kar.nic.in), IBPS (ibps.in), Employment News (employmentnews.gov.in), NCS Portal (ncs.gov.in), and other official state and central government recruitment websites.</li>
              <li>All data displayed is publicly available information intended for general citizen awareness.</li>
              <li>KnowMySchemes respects the terms of use of all source websites and operates in compliance with applicable Indian laws including the Information Technology Act 2000.</li>
              <li>KnowMySchemes does not reproduce complete government notifications or PDFs. We display summary information and link directly to original official sources for full details.</li>
            </ul>

            <p className="font-semibold text-gray-800 mt-4">4.4 User Responsibilities for Government Jobs</p>
            <ul className="list-disc pl-5 space-y-1.5 mt-1">
              <li>Users must independently verify all job details including eligibility, qualifications, age limits, reservation criteria, and application fees on the official government website.</li>
              <li>Users are solely responsible for submitting applications correctly and on time through official government channels.</li>
              <li>KnowMySchemes bears no responsibility for missed deadlines or incorrect applications.</li>
              <li>Users should not pay any fees to third parties for government job applications. All legitimate government job applications are processed through official government portals only.</li>
            </ul>
          </Section>

          <Section icon={MessageSquare} number={5} title="OTP Verification & Account Authentication">
            <p>To ensure the security of user accounts, KnowMySchemes uses One-Time Password (OTP) based verification for registration and login.</p>

            <p className="font-semibold text-gray-800 mt-4">5.1 How OTP Works</p>
            <ul className="list-disc pl-5 space-y-1.5 mt-1">
              <li>When you register or login, a 4-digit OTP will be sent to your registered mobile number via SMS.</li>
              <li>The OTP is valid for 5 minutes only. After expiry a new OTP must be requested.</li>
              <li>OTPs are sent through our registered SMS infrastructure as per TRAI DLT regulations.</li>
              <li>Standard SMS charges may apply as per your mobile operator plan.</li>
            </ul>

            <p className="font-semibold text-gray-800 mt-4">5.2 User Responsibilities</p>
            <ul className="list-disc pl-5 space-y-1.5 mt-1">
              <li>Never share your OTP with anyone including KnowMySchemes support staff.</li>
              <li>KnowMySchemes will <strong>NEVER</strong> ask for your OTP over call, email, or chat.</li>
              <li>If you receive an OTP without initiating a login, change your password immediately and contact support.</li>
              <li>Multiple failed OTP attempts may result in temporary account lockout for 30 minutes for security purposes.</li>
            </ul>

            <p className="font-semibold text-gray-800 mt-4">5.3 OTP Data</p>
            <ul className="list-disc pl-5 space-y-1.5 mt-1">
              <li>OTPs are not stored after verification is complete.</li>
              <li>Mobile numbers used for OTP are stored securely as part of your account credentials.</li>
              <li>OTP logs are maintained for fraud prevention and security audit purposes only.</li>
            </ul>
          </Section>

          <Section icon={CreditCard} number={6} title="Subscription Plans & Payment">
            <p>The platform provides both free and premium access:</p>
            <ul className="list-disc pl-5 space-y-1.5 mt-2">
              <li>Free users may access limited content, basic eligibility information, and a limited number of KMS Smart analyses.</li>
              <li>Premium users receive unlimited KMS Smart analyses, priority support, and full assisted services.</li>
              <li>Current subscription plan: <strong>Rs. 1,499 for 3 years</strong> (subject to change with prior notice).</li>
              <li>Subscriptions are personal, non-transferable, and time-bound.</li>
              <li>Payments are processed securely through Razorpay payment gateway.</li>
              <li>KMS does not store card or banking information.</li>
              <li>Subscription period begins from the date of successful payment and expires after 3 years (1,095 days).</li>
            </ul>
          </Section>

          <Section icon={RefreshCw} number={7} title="Refund Policy">
            <p>Please read our refund policy carefully before making any payment:</p>
            <ul className="list-disc pl-5 space-y-1.5 mt-2">
              <li>Service fees are <strong>NON-REFUNDABLE</strong> once application assistance has commenced.</li>
              <li>If KMS fails to deliver the agreed service due to a fault on our end, a full refund will be issued within 7 working days.</li>
              <li>Subscription cancellation mid-term will not attract a refund for the remaining period.</li>
              <li>Refund requests must be raised within 48 hours of payment through official support channels.</li>
              <li>Refunds, if applicable, will be credited to the original payment method within 7-10 working days.</li>
              <li>No refund will be issued for government rejection of applications as this is beyond KMS control.</li>
            </ul>
          </Section>

          <Section icon={Bot} number={8} title="AI-Powered Features & Training Data">
            <p className="font-semibold text-gray-800">8.1 AI Services Used</p>
            <ul className="list-disc pl-5 space-y-1.5 mt-1">
              <li>Our proprietary AI technology — for generating personalised policy impact analysis and scheme recommendations.</li>
              <li>Trusted third-party news sources — for fetching latest government policy news and budget updates.</li>
              <li>All AI responses are informational only and should not be treated as professional legal or financial advice.</li>
            </ul>

            <p className="font-semibold text-gray-800 mt-4">8.2 Anonymous Training Data Collection</p>
            <ul className="list-disc pl-5 space-y-1.5 mt-1">
              <li>With your consent, KnowMySchemes may collect anonymous usage data from KMS Smart analyses to improve our AI systems.</li>
              <li>Data collected includes: occupation type, location (district/state), policy event selected, language preference, and AI response quality metrics.</li>
              <li>We do NOT collect or store your name, phone number, email, or any personally identifiable information for training purposes.</li>
              <li>Only anonymised, aggregated data is used for AI improvement.</li>
              <li>You may opt out of training data collection at any time through your profile settings.</li>
              <li>Opting out does not affect your access to any KnowMySchemes features.</li>
            </ul>
          </Section>

          <Section icon={Database} number={9} title="Data Collection & Privacy">
            <p className="font-semibold text-gray-800">9.1 Information We Collect</p>
            <ul className="list-disc pl-5 space-y-1.5 mt-1">
              <li>Personal information: name, contact details, address, date of birth, demographic data, income information, and category details.</li>
              <li>Occupation information: your profession, business type, or educational background for personalised scheme matching.</li>
              <li>Documents: identification proofs and certificates voluntarily uploaded for service facilitation.</li>
              <li>Technical data: IP address, device data, app usage logs, platform type (website/iOS/Android), and login history.</li>
              <li>Payment data: transaction references and subscription status for accounting and verification.</li>
              <li>OTP logs: kept for security and fraud prevention purposes only.</li>
            </ul>

            <p className="font-semibold text-gray-800 mt-4">9.2 How We Use Your Information</p>
            <ul className="list-disc pl-5 space-y-1.5 mt-1">
              <li>To deliver services, assess eligibility, and verify documents.</li>
              <li>To power KMS Smart AI analysis with personalised scheme recommendations.</li>
              <li>To manage accounts, process subscriptions, and send notifications.</li>
              <li>To provide customer support and improve platform functionality.</li>
              <li>To comply with legal requirements and prevent fraud.</li>
            </ul>

            <p className="font-semibold text-gray-800 mt-4">9.3 Data Sharing</p>
            <p>User information is NOT sold or rented. Information may be shared with:</p>
            <ul className="list-disc pl-5 space-y-1.5 mt-1">
              <li>Government portals — only with explicit user consent</li>
              <li>Payment processors (Razorpay) for transaction processing</li>
              <li>SMS service providers for OTP delivery only</li>
              <li>AI service providers for analysis generation — no personally identifiable data is shared</li>
              <li>Technology service providers for platform operations</li>
              <li>Regulatory authorities where legally required</li>
            </ul>
          </Section>

          <Section icon={Lock} number={10} title="Data Retention & Security">
            <ul className="list-disc pl-5 space-y-1.5">
              <li>Account data is retained while the account is active.</li>
              <li>Documents are stored as required for service delivery and legal compliance.</li>
              <li>OTP logs are retained for 90 days for security audit purposes then permanently deleted.</li>
              <li>Anonymous training data may be retained indefinitely in aggregated form.</li>
              <li>After retention periods expire, data is securely deleted or anonymized.</li>
              <li>Security measures include encryption, access controls, secure servers, and monitoring systems.</li>
              <li>No system can guarantee complete security. Users access the platform at their own risk.</li>
            </ul>
          </Section>

          <Section icon={Shield} number={11} title="User Rights">
            <p>Users have the right to:</p>
            <ul className="space-y-1.5 mt-2">
              <li className="flex items-start gap-2"><span className="text-green-600 font-bold">&#10003;</span> <span>Access, correct, update, or delete their personal data.</span></li>
              <li className="flex items-start gap-2"><span className="text-green-600 font-bold">&#10003;</span> <span>Request restriction of data processing.</span></li>
              <li className="flex items-start gap-2"><span className="text-green-600 font-bold">&#10003;</span> <span>Obtain copies of their data in a portable format.</span></li>
              <li className="flex items-start gap-2"><span className="text-green-600 font-bold">&#10003;</span> <span>Object to certain processing activities.</span></li>
              <li className="flex items-start gap-2"><span className="text-green-600 font-bold">&#10003;</span> <span>Withdraw consent for anonymous training data collection at any time.</span></li>
              <li className="flex items-start gap-2"><span className="text-green-600 font-bold">&#10003;</span> <span>Opt out of SMS communications (except critical OTP messages required for login).</span></li>
            </ul>
            <p className="mt-3">
              Requests may be submitted through official support channels and will be addressed within 30 days as per legal timelines.
            </p>
          </Section>

          <Section icon={Cookie} number={12} title="Cookies & Tracking">
            <p>
              KMS may use cookies and similar technologies to maintain sessions, enhance usability, and analyze platform performance. Users can manage cookie preferences through their device or browser settings.
            </p>
          </Section>

          <Section icon={AlertTriangle} number={13} title="Prohibited Activities">
            <p>The following activities are strictly prohibited:</p>
            <ul className="list-disc pl-5 space-y-1.5 mt-2">
              <li>Fraudulent activity or misrepresentation of identity.</li>
              <li>Unauthorized access to other user accounts.</li>
              <li>Copying, reproducing, or distributing platform content without permission.</li>
              <li>Account sharing between multiple individuals.</li>
              <li>Attempting to bypass OTP verification or authentication systems.</li>
              <li>Using automated bots or scripts to access KMS Smart or any platform features.</li>
              <li>Any activity that violates Indian law including the Information Technology Act 2000.</li>
            </ul>
            <p className="mt-3 font-medium text-gray-800">
              KMS reserves the right to suspend or terminate accounts for any violation without prior notice.
            </p>
          </Section>

          <Section icon={Scale} number={14} title="Limitation of Liability">
            <p>KMS shall NOT be liable for:</p>
            <ul className="list-disc pl-5 space-y-1.5 mt-2">
              <li>Government rejection of scheme applications.</li>
              <li>Processing delays by government departments.</li>
              <li>Technical failures or data loss caused by external factors.</li>
              <li>Losses arising from incorrect information provided by users.</li>
              <li>AI analysis results — KMS Smart provides informational analysis only, not professional advice.</li>
              <li>SMS delivery failures due to network issues beyond our control.</li>
              <li>Inaccuracies or outdated information in government job listings. Users must verify all job details on official government websites.</li>
              <li>Missed application deadlines arising from reliance on job listings displayed on KnowMySchemes.</li>
            </ul>
            <p className="mt-3 text-gray-600 text-sm italic">
              All services are provided on an "as-is" basis. KMS reserves the right to suspend or discontinue services for maintenance, upgrades, or policy enforcement without prior notice.
            </p>
          </Section>

          <Section icon={Scale} number={15} title="Dispute Resolution">
            <ul className="list-disc pl-5 space-y-1.5">
              <li>In case of any dispute, parties shall first attempt resolution through mutual discussion within 15 days.</li>
              <li>If unresolved, disputes shall be referred to arbitration under the Arbitration and Conciliation Act, 1996.</li>
              <li>These terms are governed by the laws of India.</li>
              <li>Jurisdiction: Courts of Guntur, Andhra Pradesh, India.</li>
            </ul>
          </Section>

          <Section icon={Mail} number={16} title="Grievance Officer">
            <p className="flex items-center gap-2">
              <Mail className="w-4 h-4 text-green-600 flex-shrink-0" />
              <span>Email: <a href="mailto:info@knowmyschemes.com" className="text-green-700 hover:text-green-800 underline font-medium">info@knowmyschemes.com</a></span>
            </p>
            <p className="mt-1">Response Time: Within 30 days of receiving the complaint</p>
          </Section>

          <Section icon={Bell} number={17} title="Changes to Terms">
            <p>
              KMS reserves the right to modify these Terms, Conditions, and Privacy Policy at any time. Updated versions will be published on the website and application. Continued use of the platform after updates constitutes acceptance of the revised terms.
            </p>
          </Section>

          <Section icon={Mail} number={18} title="Contact Us">
            <p>For questions, concerns, data requests, or complaints:</p>
            <div className="mt-3 space-y-2">
              <p className="flex items-center gap-2">
                <Globe className="w-4 h-4 text-green-600 flex-shrink-0" />
                <span>Website: <a href="https://www.knowmyschemes.com" className="text-green-700 hover:text-green-800 underline font-medium">www.knowmyschemes.com</a></span>
              </p>
              <p className="flex items-center gap-2">
                <Mail className="w-4 h-4 text-green-600 flex-shrink-0" />
                <span>Email: <a href="mailto:support@knowmyschemes.com" className="text-green-700 hover:text-green-800 underline font-medium">support@knowmyschemes.com</a></span>
              </p>
              <p className="flex items-center gap-2">
                <Mail className="w-4 h-4 text-green-600 flex-shrink-0" />
                <span>Grievance: <a href="mailto:info@knowmyschemes.com" className="text-green-700 hover:text-green-800 underline font-medium">info@knowmyschemes.com</a></span>
              </p>
            </div>
          </Section>

          {/* Acceptance */}
          <div className="mt-8 pt-6 border-t border-gray-200">
            <p className="text-gray-700 text-sm leading-relaxed mb-4 font-medium">
              By using KnowMySchemes, you acknowledge that you have read, understood, and agreed to these Terms, Conditions, and Privacy Policy and consent to the collection, processing, and use of your personal data as described herein.
            </p>
            <p className="text-center text-green-700 font-semibold text-sm italic">
              KnowMySchemes — Helping Citizens Access What They Deserve
            </p>
            <p className="text-sm text-gray-500 text-center mt-2">
              www.knowmyschemes.com | info@knowmyschemes.com
            </p>
            <p className="text-sm text-gray-500 text-center mt-4">
              &copy; {new Date().getFullYear()} KnowMySchemes. All rights reserved.
            </p>
          </div>
        </div>
      </div>
      <Footer />
    </div>
  );
};

export default TermsAndConditions;
